import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Snackbar,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";

import { useAuth } from "../../context/AuthContext";
import api, { getCurrentMode } from "../../utils/api";
import pinnedBoardService from "../../services/pinnedBoardService";
import boardService from "../../services/boardService";

// Import dashboard components
import DashboardHeader from "./components/DashboardHeader";
import MetricsPanel from "./components/MetricsPanel";
import RecruitingBoardGrid from "./components/RecruitingBoardGrid";

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`board-tabpanel-${index}`}
      aria-labelledby={`board-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}{" "}
      {/* Reduced padding from 2 to 1 */}
    </div>
  );
}

const EnhancedDashboard = () => {
  const { user } = useAuth();

  // State for loading
  const [loading, setLoading] = useState(true);

  // State for active board tab
  const [boardTabValue, setBoardTabValue] = useState(0);

  // State for date range
  const [dateRange, setDateRange] = useState("30days");

  // State for more menu
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);

  // State for add board dialog
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [boardFormData, setBoardFormData] = useState({
    name: "",
    description: "",
    template: "offerStatus",
  });

  // State for error handling
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalProspects: 0,
      newProspects: 0,
    },
    boards: [
      {
        id: "main",
        name: "Main Board",
        columns: [],
      },
    ],
    pinnedBoards: [],
    aiInsights: [],
    activities: [],
    upcomingEvents: [],
  });

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (getCurrentMode() === "sandbox") {
          // Sandbox mode - use mock data
          await new Promise((resolve) => setTimeout(resolve, 1500));

          // Mock data for the dashboard
          const mockData = {
            metrics: {
              totalProspects: 156,
              newProspects: 12,
            },
            boards: [
              {
                id: "main",
                name: "Main Board",
                columns: [
                  {
                    id: "prospect",
                    title: "Prospects",
                    count: 45,
                    color: "#9e9e9e",
                    recruits: [
                      {
                        id: "1",
                        firstName: "John",
                        lastName: "Doe",
                        position: "QB",
                        highSchool: "Lincoln High",
                        state: "CA",
                        stars: 4,
                        aiRecommended: true,
                        hasNewActivity: true,
                        image: null,
                      },
                      {
                        id: "2",
                        firstName: "Mike",
                        lastName: "Smith",
                        position: "RB",
                        highSchool: "Washington High",
                        state: "TX",
                        stars: 3,
                        aiRecommended: false,
                        hasNewActivity: false,
                        image: null,
                      },
                      {
                        id: "3",
                        firstName: "Sarah",
                        lastName: "Johnson",
                        position: "PG",
                        highSchool: "Jefferson High",
                        state: "FL",
                        stars: 5,
                        aiRecommended: true,
                        hasNewActivity: false,
                        image: null,
                      },
                    ],
                  },
                  {
                    id: "contacted",
                    title: "Contacted",
                    count: 32,
                    color: "#2196f3",
                    recruits: [
                      {
                        id: "4",
                        firstName: "James",
                        lastName: "Williams",
                        position: "WR",
                        highSchool: "Central High",
                        state: "TX",
                        stars: 4,
                        aiRecommended: false,
                        hasNewActivity: true,
                        image: null,
                      },
                      {
                        id: "5",
                        firstName: "Emily",
                        lastName: "Brown",
                        position: "SF",
                        highSchool: "Eastside High",
                        state: "CA",
                        stars: 3,
                        aiRecommended: false,
                        hasNewActivity: false,
                        image: null,
                      },
                    ],
                  },
                  {
                    id: "interested",
                    title: "Interested",
                    count: 28,
                    color: "#ff9800",
                    recruits: [
                      {
                        id: "6",
                        firstName: "David",
                        lastName: "Miller",
                        position: "C",
                        highSchool: "Westview High",
                        state: "FL",
                        stars: 5,
                        aiRecommended: true,
                        hasNewActivity: true,
                        image: null,
                      },
                    ],
                  },
                ],
              },
            ],
            pinnedBoards: [
              {
                id: "pinned-main",
                name: "Pinned Main Board",
                boardId: "main",
              },
            ],
            aiInsights: [
              {
                id: 1,
                type: "recruitment",
                message: "Consider reaching out to QB prospects in Texas",
                priority: "high",
              },
            ],
            activities: [
              {
                id: 1,
                type: "prospect_added",
                message: "New prospect John Doe added to board",
                timestamp: new Date().toISOString(),
              },
            ],
            upcomingEvents: [
              {
                id: 1,
                title: "Recruiting Visit",
                date: "2024-02-15",
                location: "Lincoln High School",
              },
            ],
          };

          setDashboardData(mockData);
        } else {
          // Production mode - fetch from API
          try {
            // Only fetch pinned boards for now, skip metrics
            const pinnedBoardsResponse =
              await pinnedBoardService.getPinnedBoards();

            // Extract the pinned boards data
            const pinnedBoardsData = pinnedBoardsResponse || [];

            // Map pinned boards to the boards array format for display
            const mappedBoards = pinnedBoardsData.map((pinnedBoard) => ({
              id: pinnedBoard._id,
              name: pinnedBoard.name,
              description: pinnedBoard.description,
              columns:
                pinnedBoard.columns?.map((column) => ({
                  id: column._id,
                  title: column.title,
                  color: column.color,
                  recruits:
                    column.recruits?.map((recruit) => ({
                      id:
                        recruit.prospect?._id ||
                        recruit._id ||
                        `recruit-${Date.now()}`,
                      // CRITICAL: Preserve the prospect field for notes functionality
                      prospect: recruit.prospect,
                      firstName:
                        recruit.firstName || recruit.prospect?.firstName,
                      lastName: recruit.lastName || recruit.prospect?.lastName,
                      position: recruit.position || recruit.prospect?.position,
                      highSchool:
                        recruit.highSchool || recruit.prospect?.highSchool,
                      city: recruit.city || recruit.prospect?.address?.city,
                      state: recruit.state || recruit.prospect?.address?.state,
                      stars: 3, // Default value since it's not in the data
                      aiRecommended: false, // Default value
                      hasNewActivity: false, // Default value
                      image: null, // Default value
                      color: recruit.color,
                      classYear:
                        recruit.classYear ||
                        recruit.prospect?.graduationYear?.toString(),
                      addedAt: recruit.addedAt,
                    })) || [],
                  order: column.order || 0,
                })) || [],
            }));

            // Fetch metrics data
            let metricsData = { totalProspects: 0, newProspects: 0 };
            try {
              // Get total prospects count
              const prospectsResponse = await api.prospects.get();

              // Handle different response formats from API
              let totalCount = 0;
              let allProspects = [];

              if (prospectsResponse.success && prospectsResponse.data) {
                // Backend format: { success: true, data: [...], total: X, count: X }
                totalCount =
                  prospectsResponse.total || prospectsResponse.count || 0;
                allProspects = prospectsResponse.data;
              } else if (Array.isArray(prospectsResponse)) {
                // Direct array format
                totalCount = prospectsResponse.length;
                allProspects = prospectsResponse;
              } else if (
                prospectsResponse.data &&
                Array.isArray(prospectsResponse.data)
              ) {
                // Alternative format
                totalCount = prospectsResponse.data.length;
                allProspects = prospectsResponse.data;
              }

              metricsData.totalProspects = totalCount;

              // Get new prospects (created in last 30 days)
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

              // Filter prospects created in last 30 days
              const newProspects = allProspects.filter((prospect) => {
                if (!prospect.createdAt) return false;
                const createdAt = new Date(prospect.createdAt);
                return (
                  !isNaN(createdAt.getTime()) && createdAt >= thirtyDaysAgo
                );
              });
              metricsData.newProspects = newProspects.length;

              console.log("Fetched metrics:", metricsData);
              console.log("Total prospects:", totalCount);
              console.log("New prospects (30 days):", newProspects.length);
            } catch (metricsError) {
              console.error("Error fetching metrics:", metricsError);
            }

            const newDashboardData = {
              metrics: metricsData,
              boards: mappedBoards, // Use the mapped boards instead of empty array
              pinnedBoards: pinnedBoardsData,
              aiInsights: [], // Keep empty for now - would need AI service
              activities: [], // Keep empty for now - would need activity tracking
              upcomingEvents: [], // Keep empty for now - would need event service
            };

            setDashboardData(newDashboardData);
          } catch (apiError) {
            console.error("Error fetching pinned boards:", apiError);
            setLoading(false);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Handle board tab change
  const handleBoardTabChange = (event, newValue) => {
    setBoardTabValue(newValue);
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    // In a real app, you would fetch new data based on the date range
  };

  // Handle more menu open
  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  // Handle opening add board dialog
  const handleAddBoardDialogOpen = () => {
    setBoardFormData({
      name: "",
      description: "",
      template: "offerStatus",
    });
    setBoardDialogOpen(true);
    handleMoreMenuClose(); // Close the menu when opening dialog
  };

  // Handle closing board dialog
  const handleBoardDialogClose = () => {
    setBoardDialogOpen(false);
    setError(null);
  };

  // Handle board form input change
  const handleBoardFormChange = (e) => {
    const { name, value } = e.target;
    setBoardFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle save board
  const handleSaveBoard = async () => {
    if (boardFormData.name.trim() === "") {
      setError("Board name is required");
      return;
    }

    setError(null);

    try {
      // Add new board using boardService
      const newBoardData = {
        name: boardFormData.name.trim(),
        description: boardFormData.description.trim(),
        template: boardFormData.template,
      };

      const newBoard = await boardService.createBoard(newBoardData);

      // Add the new board to dashboard data
      setDashboardData((prev) => ({
        ...prev,
        boards: [...prev.boards, newBoard],
      }));

      // Switch to the new board tab
      setBoardTabValue(dashboardData.boards.length);

      // Show success message
      setSnackbar({
        open: true,
        message: "Board created successfully",
        severity: "success",
      });

      handleBoardDialogClose();
    } catch (error) {
      console.error("Error saving board:", error);
      setError("Failed to save board. Please try again.");
    }
  };
  // Handle delete board
  const handleDeleteBoard = () => {
    if (boardTabValue >= 0 && dashboardData.boards.length > 0) {
      const currentBoard = dashboardData.boards[boardTabValue];
      const updatedBoards = dashboardData.boards.filter(
        (_, index) => index !== boardTabValue
      );

      // Update dashboard data with the board removed
      setDashboardData({
        ...dashboardData,
        boards: updatedBoards,
      });

      // If we deleted the last board, select the new last board
      if (boardTabValue >= updatedBoards.length) {
        setBoardTabValue(Math.max(0, updatedBoards.length - 1));
      }

      // Close the menu
      handleMoreMenuClose();

      // Show success message in console (in a real app, you would show a UI notification)
      console.log(`Board "${currentBoard.name}" deleted successfully`);
      alert(`Board "${currentBoard.name}" deleted successfully`);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      await refreshPinnedBoards();
    } catch (error) {
      console.error("Error refreshing dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh pinned boards data
  const refreshPinnedBoards = async () => {
    try {
      const pinnedBoardsResponse = await pinnedBoardService.getPinnedBoards();
      const pinnedBoardsData = pinnedBoardsResponse || [];

      // Map pinned boards to the boards array format for display
      const mappedBoards = pinnedBoardsData.map((pinnedBoard) => ({
        id: pinnedBoard._id,
        name: pinnedBoard.name,
        description: pinnedBoard.description,
        columns:
          pinnedBoard.columns?.map((column) => ({
            id: column._id,
            title: column.title,
            color: column.color,
            recruits:
              column.recruits?.map((recruit) => ({
                id:
                  recruit.prospect?._id ||
                  recruit._id ||
                  `recruit-${Date.now()}`,
                // CRITICAL: Preserve the prospect field for notes functionality
                prospect: recruit.prospect,
                firstName: recruit.firstName || recruit.prospect?.firstName,
                lastName: recruit.lastName || recruit.prospect?.lastName,
                position: recruit.position || recruit.prospect?.position,
                highSchool: recruit.highSchool || recruit.prospect?.highSchool,
                city: recruit.city || recruit.prospect?.address?.city,
                state: recruit.state || recruit.prospect?.address?.state,
                stars: 3, // Default value since it's not in the data
                aiRecommended: false, // Default value
                hasNewActivity: false, // Default value
                image: null, // Default value
                color: recruit.color,
                classYear:
                  recruit.classYear ||
                  recruit.prospect?.graduationYear?.toString(),
                addedAt: recruit.addedAt,
              })) || [],
            order: column.order || 0,
          })) || [],
      }));

      setDashboardData((prev) => ({
        ...prev,
        boards: mappedBoards,
        pinnedBoards: pinnedBoardsData,
      }));
    } catch (error) {
      console.error("Error refreshing pinned boards:", error);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 0.5, mb: 1, px: 0.5 }}>
      {" "}
      {/* Removed Container, reduced margins and added minimal horizontal padding */}
      {/* Dashboard Header */}
      <DashboardHeader
        user={user}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={handleRefresh}
      />
      {/* Main Content */}
      <Grid container spacing={1.5}>
        {" "}
        {/* Reduced spacing from 3 to 1.5 */}
        {/* Metrics Panel */}
        <Grid item xs={12}>
          <MetricsPanel
            metrics={dashboardData.metrics}
            activities={dashboardData.activities}
            loading={loading}
          />
        </Grid>
        {/* Board Tabs and Controls */}
        {dashboardData.boards && dashboardData.boards.length > 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 1.5, // Reduced from 2 to 1.5
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Tabs
                  value={boardTabValue}
                  onChange={handleBoardTabChange}
                  aria-label="recruiting board tabs"
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {dashboardData.boards.map((board) => (
                    <Tab
                      key={board.id}
                      label={board.name}
                      id={`board-tab-${board.id}`}
                      aria-controls={`board-tabpanel-${board.id}`}
                    />
                  ))}
                </Tabs>

                <Box>
                  <IconButton
                    size="small"
                    aria-label="more options"
                    onClick={handleMoreMenuOpen}
                    sx={{ ml: 0.5 }} // Reduced from 1 to 0.5
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={moreMenuAnchorEl}
                    open={Boolean(moreMenuAnchorEl)}
                    onClose={handleMoreMenuClose}
                  >
                    <Divider />
                    <MenuItem onClick={handleAddBoardDialogOpen}>
                      Create New Board
                    </MenuItem>
                    <MenuItem
                      onClick={handleDeleteBoard}
                      sx={{ color: "error.main" }}
                      disabled={dashboardData.boards.length <= 1}
                    >
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete Board
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>

              {/* Board Content */}
              {dashboardData.boards.map((board, index) => (
                <TabPanel key={board.id} value={boardTabValue} index={index}>
                  <RecruitingBoardGrid
                    columns={board.columns}
                    onColumnsUpdate={async (updatedColumns) => {
                      try {
                        // Update the board in the backend
                        const updatedBoard = await boardService.updateBoard(
                          board.id,
                          {
                            columns: updatedColumns,
                          }
                        );

                        // Update local state with the response from backend
                        const updatedBoards = dashboardData.boards.map((b) =>
                          b.id === board.id ? updatedBoard : b
                        );

                        // Update the boards state
                        setDashboardData({
                          ...dashboardData,
                          boards: updatedBoards,
                        });

                        // If this board is pinned, also update the pinned boards state
                        const updatedPinnedBoards =
                          dashboardData.pinnedBoards.map((pinnedBoard) => {
                            if (
                              pinnedBoard._id === board.id ||
                              pinnedBoard.id === board.id
                            ) {
                              return updatedBoard;
                            }
                            return pinnedBoard;
                          });

                        setDashboardData((prev) => ({
                          ...prev,
                          pinnedBoards: updatedPinnedBoards,
                        }));

                        refreshPinnedBoards(); // Refresh pinned boards after board update
                      } catch (error) {
                        console.error("Error updating board:", error);
                        // You could show a toast notification here
                      }
                    }}
                    onRecruitUpdate={async (recruitId, updates) => {
                      try {
                        // Find the column that contains this recruit
                        let columnId = null;
                        for (const column of board.columns) {
                          if (
                            column.recruits?.some((r) => r.id === recruitId)
                          ) {
                            columnId = column.id;
                            break;
                          }
                        }

                        if (columnId) {
                          // Use the correct board ID (could be _id or id)
                          const boardId = board._id || board.id;

                          // Update the individual recruit in the backend
                          await boardService.updateRecruit(
                            boardId,
                            columnId,
                            recruitId,
                            updates
                          );

                          // Refresh the pinned boards to get the updated data
                          await refreshPinnedBoards();
                        }
                      } catch (error) {
                        console.error("Error updating recruit:", error);
                      }
                    }}
                  />
                </TabPanel>
              ))}
            </Paper>
          </Grid>
        ) : (
          // Show message when no boards are available
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Boards Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Here you'll be able to see your pinned boards. You can pin an
                existing board from the boards section or create a new one.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* Add Board Dialog */}
      <Dialog
        open={boardDialogOpen}
        onClose={handleBoardDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Board</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Board Name"
            type="text"
            fullWidth
            variant="outlined"
            value={boardFormData.name}
            onChange={handleBoardFormChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={boardFormData.description}
            onChange={handleBoardFormChange}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">Template</FormLabel>
            <RadioGroup
              row
              name="template"
              value={boardFormData.template}
              onChange={handleBoardFormChange}
            >
              <FormControlLabel
                value="offerStatus"
                control={<Radio />}
                label="Offer Status"
              />
              <FormControlLabel
                value="blank"
                control={<Radio />}
                label="Blank Board"
              />
              <FormControlLabel
                value="fivePositions"
                control={<Radio />}
                label="5 Positions (RB, LB, QB, TE, WR)"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBoardDialogClose}>Cancel</Button>
          <Button
            onClick={handleSaveBoard}
            variant="contained"
            disabled={!boardFormData.name.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedDashboard;
